import React from 'react';
import { useDevAutobattler } from './useDevAutobattler';
import BattleUI from '../BattleUI';
import './DevAutobattlerUI.css';

/**
 * Dev Autobattler UI Component
 * Wrapper that uses BattleUI in spectator mode for bot vs bot battles with config control
 */
function DevAutobattlerUI({ ws, userId, initialGameState = null, initialGameId = null, initialTurn = 1, token, onLogout }) {
  const {
    gameState,
    gameId,
    currentTurn,
    turnIndex,
    isLoading,
    error,
    logFilePath,
    canAdvanceTurn,
    startDevAutobattler,
    nextTurn,
    reloadConfig
  } = useDevAutobattler(ws, userId, initialGameState, initialGameId, initialTurn);

  // If no game started, show start button
  if (!gameState || !gameId) {
    return (
      <div className="dev-autobattler-container">
        <div className="dev-autobattler-start-screen">
          <h1>Dev Autobattler Mode</h1>
          <p>Test orders systematically with config-controlled bot states</p>
          {error && <div className="dev-autobattler-error">{error}</div>}
          <button 
            onClick={startDevAutobattler}
            disabled={isLoading}
            className="dev-autobattler-start-button"
          >
            {isLoading ? 'Starting...' : 'Start Dev Autobattler'}
          </button>
        </div>
      </div>
    );
  }

  const hasWebSocket = !!ws;
  const isGameEnded = gameState.gameEnded || gameState.winner;

  return (
    <div className="dev-autobattler-wrapper">
      {/* Controls overlay */}
      <div className="dev-autobattler-controls-overlay">
        <div className="dev-autobattler-turn-info">
          <span>Turn {currentTurn}</span>
          {turnIndex !== null && turnIndex !== undefined && (
            <span className="dev-autobattler-turn-index">
              (Bot {turnIndex % 2 === 0 ? '1' : '2'})
            </span>
          )}
          {isGameEnded && gameState.winner && (
            <span className="dev-autobattler-winner">
              Winner: {gameState.winner}
            </span>
          )}
        </div>
        {error && <div className="dev-autobattler-error">{error}</div>}
        {logFilePath && (
          <div className="dev-autobattler-log-info">
            Log: {logFilePath.split('/').pop() || logFilePath.split('\\').pop()}
          </div>
        )}
        <div className="dev-autobattler-controls">
          <button
            onClick={() => {
              if (!hasWebSocket || !gameId) {
                console.error('Cannot advance turn: ws=', hasWebSocket, 'gameId=', gameId);
                return;
              }
              nextTurn();
            }}
            disabled={!canAdvanceTurn || isLoading || !hasWebSocket || !gameId || isGameEnded}
            className="dev-autobattler-button dev-autobattler-button-next"
          >
            {isLoading ? 'Processing...' : 'Next Turn'}
          </button>
          <button
            onClick={() => {
              if (!hasWebSocket || !gameId) {
                console.error('Cannot reload config: ws=', hasWebSocket, 'gameId=', gameId);
                return;
              }
              reloadConfig();
            }}
            disabled={isLoading || !hasWebSocket || !gameId}
            className="dev-autobattler-button dev-autobattler-button-reload"
          >
            {isLoading ? 'Reloading...' : 'Reload Config'}
          </button>
        </div>
      </div>

      {/* Use BattleUI - it will render the game state we provide */}
      <div className="dev-autobattler-battle-container">
        <BattleUI
          user={userId}
          userId={userId}
          token={token}
          onLogout={onLogout}
          initialGameState={gameState}
          initialGameId={gameId}
          isAutobattler={true}
          autobattlerWs={ws}
        />
      </div>
    </div>
  );
}

export default DevAutobattlerUI;
